import React from "react";
import { Box, Dialog, DialogContent, Grid, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import { useTranslation } from "react-i18next";
import { formatStringCurrency } from "../common/CurrencyFormatter";
import { ComeOnReceipt } from "../images/comeonicons";
import { PoolXMoney } from "../models/money";
import { Button } from "../reusable-components";
import { IsComeOn } from "../theme/themehelper";
import { CashoutResult } from "./usecashout";

const paperSx = {
    borderRadius: 'var(--radius)',
    backgroundColor: 'var(--background_2)',
    width: '300px',
    minHeight: '160px',
    padding: '12px 12px 24px 12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
};

const contentSx = { padding: '0px', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', overflow: 'visible' };

const CashoutDialogHeader: React.FC<{ onClose: () => void }> = ({ onClose }) => (
    <>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <IconButton onClick={onClose} size="small" sx={{ padding: 0 }}>
                <CloseIcon sx={{ color: 'var(--text-black)', cursor: 'pointer' }} />
            </IconButton>
        </Box>

        <Box sx={{ fontSize: '60px', margin: '5px 0px 5px 0px', display: 'flex', justifyContent: 'center' }}>
            {IsComeOn((window as any).theme) ? (
                <Grid>{ComeOnReceipt(51, 50, 'var(--text-black)')}</Grid>
            ) : (
                <ReceiptOutlinedIcon sx={{ color: 'var(--text-black)' }} fontSize="inherit" />
            )}
        </Box>
    </>
);

interface CashoutConfirmDialogProps {
    open: boolean;
    value?: PoolXMoney;
    onClose: () => void;
    onConfirm: () => void;
}

export const CashoutConfirmDialog: React.FC<CashoutConfirmDialogProps> = ({ open, value, onClose, onConfirm }) => {
    const { t } = useTranslation();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{ sx: paperSx }}
        >
            <DialogContent sx={contentSx}>
                <CashoutDialogHeader onClose={onClose} />

                <Typography sx={{
                    fontWeight: 'bold',
                    fontSize: '14px',
                    margin: '20px 0',
                    color: 'var(--text-black)',
                    textAlign: 'center',
                }}>
                    {t('cash_out_confirm', {
                        amount: value
                            ? formatStringCurrency(value.amount, value.currency, t, false, true)
                            : t('cash_out_selected_amount')
                    })}
                </Typography>

                <Button
                    width={200}
                    onClick={onConfirm}
                >
                    {t('cash_out')}
                </Button>
            </DialogContent>
        </Dialog>
    );
};

interface CashoutResultDialogProps {
    result: CashoutResult | null;
    onClose: () => void;
}

export const CashoutResultDialog: React.FC<CashoutResultDialogProps> = ({ result, onClose }) => {
    const { t } = useTranslation();

    return (
        <Dialog
            open={!!result}
            onClose={onClose}
            PaperProps={{ sx: paperSx }}
        >
            <DialogContent sx={contentSx}>
                <CashoutDialogHeader onClose={onClose} />

                <Typography sx={{
                    fontWeight: 'bold',
                    fontSize: '14px',
                    margin: '20px 0',
                    color: 'var(--text-black)',
                    textAlign: 'center',
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                }}>
                    {result?.success === false ? (
                        <>{t('cash_out_error')} {result.error}</>
                    ) : (
                        t('cash_out_completed')
                    )}
                </Typography>

                <Button
                    width={200}
                    onClick={onClose}
                >
                    {t('ok')}
                </Button>
            </DialogContent>
        </Dialog>
    );
};
